import type {
  User,
  Dealer,
  Order,
  WorkOrder,
  WorkstationQueue,
  InventoryItem,
  InventoryGroup,
  ProductLine,
  ProductLineVersion,
  DashboardStats,
  RecentActivity,
  PaginatedResponse,
  PaginationParams,
  OrderStatus,
  WorkOrderStatus,
  WorkstationStep,
  BomAssemblyItem,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

class ApiService {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    // Use instance token or fall back to localStorage (handles race condition on page load)
    const token = this.token || localStorage.getItem('admin_auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getProfile(): Promise<User> {
    return this.request('/auth/profile');
  }

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request('/dashboard/stats');
  }

  async getOrderStatusCounts(): Promise<{ counts: Record<string, number>; total: number }> {
    return this.request('/orders/stats/counts');
  }

  async getRecentActivity(limit = 10): Promise<RecentActivity[]> {
    return this.request(`/dashboard/activity?limit=${limit}`);
  }

  // Orders
  async getOrders(params: PaginationParams & {
    status?: OrderStatus;
    statusIn?: string;
    dealerId?: string;
    search?: string;
    includeArchived?: boolean;
    qbFilter?: 'needs' | 'done';
    paidFilter?: 'false' | 'outstanding' | 'partial';
    paymentMethod?: string;
    pendingAch?: boolean;
  } = {}): Promise<PaginatedResponse<Order>> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.sort) searchParams.set('sort', params.sort);
    if (params.order) searchParams.set('order', params.order);
    if (params.status) searchParams.set('status', params.status);
    if (params.statusIn) searchParams.set('statusIn', params.statusIn);
    if (params.dealerId) searchParams.set('dealerId', params.dealerId);
    if (params.search) searchParams.set('search', params.search);
    if (params.includeArchived) searchParams.set('includeArchived', 'true');
    if (params.qbFilter) searchParams.set('qbFilter', params.qbFilter);
    if (params.paidFilter) searchParams.set('paidFilter', params.paidFilter);
    if (params.paymentMethod) searchParams.set('paymentMethod', params.paymentMethod);
    if (params.pendingAch) searchParams.set('pendingAch', 'true');

    return this.request(`/orders?${searchParams.toString()}`);
  }

  async getOrder(id: string): Promise<Order> {
    return this.request(`/orders/${id}`);
  }

  async updateOrder(id: string, data: Record<string, any>): Promise<Order> {
    return this.request(`/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // RUSH toggle / surcharge waive — dedicated endpoint that works at any
  // production stage (the general PATCH /orders/:id is blocked once in production).
  async updateRush(id: string, data: { isRush?: boolean; rushSurchargeWaived?: boolean }): Promise<Order> {
    return this.request(`/orders/${id}/rush`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async setShippingMethod(id: string, method: 'shipping' | 'will_call'): Promise<any> {
    return this.request(`/orders/${id}/shipping-method`, {
      method: 'POST',
      body: JSON.stringify({ method }),
    });
  }

  // Void a purchased parcel label at the carrier (same-day only). Clears the
  // label so a corrected one can be bought.
  async voidParcelLabel(orderId: string): Promise<{ success: boolean; error?: string }> {
    return this.request(`/parcel/shipment/${orderId}/void`, { method: 'POST' });
  }

  // Pull latest FreightView tracking/PRO/BOL/status for an order (same sync the
  // shipping portal runs). Endpoint: POST /freight/fv/sync/:orderId.
  async syncFreightTracking(orderId: string): Promise<{ success: boolean; status?: string }> {
    return this.request(`/freight/fv/sync/${orderId}`, { method: 'POST' });
  }

  // Manual carrier / BOL / PRO entry, usable at any order status.
  async updateShippingTracking(
    orderId: string,
    body: { carrier?: string; bolNumber?: string; proNumber?: string; shippedAt?: string },
  ): Promise<any> {
    return this.request(`/shipping/orders/${orderId}/tracking`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async updateOrderStatus(id: string, status: OrderStatus, notes?: string): Promise<Order> {
    return this.request(`/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    });
  }

  async rejectForCorrection(id: string, reason: string): Promise<Order> {
    return this.request(`/orders/${id}/reject-for-correction`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async releaseOrder(id: string): Promise<Order> {
    return this.request(`/orders/${id}/release`, {
      method: 'POST',
    });
  }

  async pushToProduction(id: string): Promise<Order> {
    // Push order to production - this releases it and creates work orders
    return this.request(`/orders/${id}/release`, {
      method: 'POST',
    });
  }

  async updateSchedule(id: string, estimatedShipDate: string): Promise<Order> {
    return this.request(`/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ estimatedShipDate }),
    });
  }

  async clearInvoiceCorrection(id: string): Promise<Order> {
    return this.request(`/orders/${id}/admin-edit`, {
      method: 'PATCH',
      body: JSON.stringify({ clearCorrectionRequest: true }),
    });
  }

  async addOrderNote(id: string, note: string, isInternal = true): Promise<void> {
    return this.request(`/orders/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note, isInternal }),
    });
  }

  async impersonateDealer(dealerId: string, contactId?: string): Promise<{ token: string; dealer: any }> {
    const qs = contactId ? `?contactId=${contactId}` : '';
    return this.request(`/auth/impersonate/${dealerId}${qs}`, {
      method: 'POST',
    });
  }

  async impersonateSalesRep(salesRepId: string): Promise<{ token: string; dealer: any }> {
    return this.request(`/auth/impersonate-sales-rep/${salesRepId}`, {
      method: 'POST',
    });
  }

  async getDealerTeam(dealerId: string): Promise<{ contacts: any[]; salesReps: any[] }> {
    return this.request(`/auth/dealer-team/${dealerId}`);
  }

  async approveWithoutDeposit(id: string): Promise<Order> {
    return this.request(`/orders/${id}/approve-without-deposit`, {
      method: 'POST',
    });
  }

  async recordPayment(id: string, data: { amount: number; payment_type: string; payment_method: string; payment_reference?: string; notes?: string }): Promise<Order> {
    return this.request(`/orders/${id}/record-payment`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async adminEditOrder(id: string, data: any): Promise<Order> {
    return this.request(`/orders/${id}/admin-edit`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async adminEditItem(orderId: string, itemId: string, data: any): Promise<any> {
    return this.request(`/orders/${orderId}/items/${itemId}/admin-edit`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Dealers
  async getDealers(params: PaginationParams & {
    status?: string;
    search?: string;
  } = {}): Promise<PaginatedResponse<Dealer>> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.status) searchParams.set('status', params.status);
    if (params.search) searchParams.set('search', params.search);
    
    return this.request(`/dealers?${searchParams.toString()}`);
  }

  async getDealer(id: string): Promise<Dealer> {
    return this.request(`/dealers/${id}`);
  }

  async createDealer(data: Partial<Dealer>): Promise<Dealer> {
    return this.request('/dealers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDealer(id: string, data: Partial<Dealer>): Promise<Dealer> {
    return this.request(`/dealers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateDealerStatus(id: string, status: 'active' | 'inactive'): Promise<Dealer> {
    return this.request(`/dealers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async deleteDealer(id: string): Promise<{ deleted: boolean; deactivated: boolean; message?: string }> {
    return this.request(`/dealers/${id}`, { method: 'DELETE' });
  }

  async getDealerOrders(id: string, params: PaginationParams = {}): Promise<PaginatedResponse<Order>> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    
    return this.request(`/dealers/${id}/orders?${searchParams.toString()}`);
  }

  // Production
  async getWorkstationQueues(): Promise<WorkstationQueue[]> {
    return this.request('/production/queues');
  }

  async getOrdersInProduction(): Promise<any[]> {
    return this.request('/production/orders-in-production');
  }

  async getOrderLaborBom(orderId: string): Promise<any> {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/production/orders/${orderId}/labor-bom`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) return { items: [], summary: { totalMinutes: 0, totalCost: 0, totalWorkOrders: 0, completedWorkOrders: 0 } };
    return response.json();
  }

  async getWorkstationQueue(workstation: WorkstationStep): Promise<WorkstationQueue> {
    return this.request(`/production/queues/${workstation}`);
  }

  async getWorkOrders(params: PaginationParams & {
    status?: WorkOrderStatus;
    workstation?: WorkstationStep;
    orderId?: string;
  } = {}): Promise<PaginatedResponse<WorkOrder>> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.status) searchParams.set('status', params.status);
    if (params.workstation) searchParams.set('workstation', params.workstation);
    if (params.orderId) searchParams.set('orderId', params.orderId);
    
    return this.request(`/production/work-orders?${searchParams.toString()}`);
  }

  async getWorkOrder(id: string): Promise<WorkOrder> {
    return this.request(`/production/work-orders/${id}`);
  }

  async updateWorkOrderStatus(id: string, status: WorkOrderStatus): Promise<WorkOrder> {
    return this.request(`/production/work-orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async blockWorkOrder(id: string, blockerType: string, reason: string): Promise<WorkOrder> {
    return this.request(`/production/work-orders/${id}/block`, {
      method: 'POST',
      body: JSON.stringify({ blockerType, reason }),
    });
  }

  async unblockWorkOrder(id: string): Promise<WorkOrder> {
    return this.request(`/production/work-orders/${id}/unblock`, {
      method: 'POST',
    });
  }

  async completeWorkstationStep(workOrderId: string, workstation: WorkstationStep): Promise<WorkOrder> {
    return this.request(`/production/work-orders/${workOrderId}/complete-step`, {
      method: 'POST',
      body: JSON.stringify({ workstation }),
    });
  }

  // Inventory
  inventory = {
    // Get all inventory items
    getAll: async (params: { limit?: number; group?: string; search?: string } = {}): Promise<InventoryItem[]> => {
      const searchParams = new URLSearchParams();
      searchParams.set('limit', (params.limit || 10000).toString());
      if (params.group) searchParams.set('group', params.group);
      if (params.search) searchParams.set('search', params.search);
      const response = await this.request<{ items: InventoryItem[]; total: number } | InventoryItem[]>(`/inventory?${searchParams.toString()}`);
      // API returns { items: [], total, ... } but we need just the array
      return Array.isArray(response) ? response : (response.items || []);
    },

    // Get inventory groups for filter dropdown (legacy - returns distinct group names from items)
    getGroups: async (): Promise<{ inventory_group: string }[]> => {
      return this.request('/inventory/groups');
    },

    // Get inventory groups from inventory_groups table (new - with full details)
    getInventoryGroups: async (includeInactive = false): Promise<InventoryGroup[]> => {
      return this.request(`/inventory-groups?includeInactive=${includeInactive}`);
    },

    // Get product lines
    getProductLines: async (includeInactive = false): Promise<ProductLine[]> => {
      return this.request(`/product-lines?includeInactive=${includeInactive}`);
    },

    // Get product lines with versions
    getProductLinesWithVersions: async (includeInactive = false): Promise<ProductLine[]> => {
      const productLines = await this.request(`/product-lines?includeInactive=${includeInactive}`);
      // Fetch versions for each product line
      const withVersions = await Promise.all(
        productLines.map(async (pl: ProductLine) => {
          try {
            const versions = await this.request(`/product-lines/${pl.id}/versions`);
            return { ...pl, versions };
          } catch {
            return { ...pl, versions: [] };
          }
        })
      );
      return withVersions;
    },

    // Get versions for a product line
    getProductLineVersions: async (productLineId: string): Promise<ProductLineVersion[]> => {
      return this.request(`/product-lines/${productLineId}/versions`);
    },

    // Get single item
    getItem: async (id: string): Promise<InventoryItem> => {
      return this.request(`/inventory/${id}`);
    },

    // Create new item
    create: async (data: Partial<InventoryItem>): Promise<InventoryItem> => {
      return this.request('/inventory', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // Update item
    update: async (id: string, data: Partial<InventoryItem>): Promise<InventoryItem> => {
      return this.request(`/inventory/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    // Adjust stock (quantityOnHand)
    adjustStock: async (id: string, quantityOnHand: number, notes?: string): Promise<InventoryItem> => {
      return this.request(`/inventory/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ quantityOnHand, notes }),
      });
    },

    // Delete/deactivate item
    delete: async (id: string): Promise<void> => {
      return this.request(`/inventory/${id}`, {
        method: 'DELETE',
      });
    },

    // Bulk import from CSV
    bulkImport: async (items: Partial<InventoryItem>[]): Promise<{ created: number; updated: number; errors: string[] }> => {
      return this.request('/inventory/bulk', {
        method: 'POST',
        body: JSON.stringify({ items }),
      });
    },

    // Upload image
    uploadImage: async (id: string, file: File): Promise<{ image_filename: string }> => {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await fetch(`${API_BASE}/inventory/${id}/image`, {
        method: 'POST',
        headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload image');
      }
      
      return response.json();
    },

    // Delete image
    deleteImage: async (id: string): Promise<void> => {
      const response = await fetch(`${API_BASE}/inventory/${id}/image`, {
        method: 'DELETE',
        headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete image');
      }
    },

    // Get low stock items
    getLowStock: async (): Promise<InventoryItem[]> => {
      return this.request('/inventory/low-stock');
    },

    // Legacy methods for backward compatibility
    getInventoryItems: async (params: PaginationParams & {
      category?: string;
      group?: string;
      lowStock?: boolean;
      search?: string;
    } = {}): Promise<PaginatedResponse<InventoryItem>> => {
      const searchParams = new URLSearchParams();
      searchParams.set('limit', (params.limit || 10000).toString());
      if (params.page) searchParams.set('page', params.page.toString());
      if (params.category) searchParams.set('category', params.category);
      if (params.group) searchParams.set('group', params.group);
      if (params.lowStock) searchParams.set('lowStock', 'true');
      if (params.search) searchParams.set('search', params.search);
      
      const data = await this.request<InventoryItem[]>(`/inventory?${searchParams.toString()}`);
      return { data, total: data.length, page: 1, limit: data.length, totalPages: 1 };
    },

    getLowStockItems: async (): Promise<InventoryItem[]> => {
      return this.request('/inventory/low-stock');
    },

    adjustInventory: async (id: string, params: { type: string; quantity: number; reason?: string }): Promise<InventoryItem> => {
      let newOnHand: number;
      const item = await this.request<InventoryItem>(`/inventory/${id}`);
      
      if (params.type === 'add') {
        newOnHand = (item.on_hand || 0) + params.quantity;
      } else if (params.type === 'subtract') {
        newOnHand = Math.max(0, (item.on_hand || 0) - params.quantity);
      } else {
        newOnHand = params.quantity;
      }
      
      return this.request(`/inventory/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ on_hand: newOnHand, notes: params.reason }),
      });
    },

    receiveInventory: async (params: { partNo: string; quantity: number; poNumber?: string }): Promise<InventoryItem> => {
      // Find item by part number and add quantity
      const items = await this.request<InventoryItem[]>(`/inventory?search=${params.partNo}`);
      const item = items.find(i => i.part_number === params.partNo);
      if (!item) throw new Error('Item not found');

      return this.request(`/inventory/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ on_hand: (item.on_hand || 0) + params.quantity }),
      });
    },

    // Receive inventory with proper tracking and display unit support
    receiveInventoryById: async (params: {
      itemId: string;
      quantity: number;
      quantityInDisplayUnits?: boolean;
      rollCount?: number;
      poNumber?: string;
      notes?: string;
    }): Promise<any> => {
      return this.request(`/inventory/${params.itemId}/receive`, {
        method: 'POST',
        body: JSON.stringify({
          quantity: params.quantity,
          quantityInDisplayUnits: params.quantityInDisplayUnits,
          rollCount: params.rollCount,
          poNumber: params.poNumber,
          notes: params.notes,
        }),
      });
    },

    // ==================== Assembly Items ====================

    // Get assembly types
    getAssemblyTypes: async (): Promise<{ types: string[] }> => {
      return this.request('/bom/assembly-items/types');
    },

    // Get assembly items for an inventory item
    getAssemblyItems: async (inventoryItemId: string): Promise<BomAssemblyItem[]> => {
      return this.request(`/bom/assembly-items/by-inventory/${inventoryItemId}`);
    },

    // Create assembly item
    createAssemblyItem: async (data: Partial<BomAssemblyItem>): Promise<BomAssemblyItem> => {
      return this.request('/bom/assembly-items', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // Update assembly item
    updateAssemblyItem: async (id: string, data: Partial<BomAssemblyItem>): Promise<BomAssemblyItem> => {
      return this.request(`/bom/assembly-items/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    // Delete assembly item
    deleteAssemblyItem: async (id: string): Promise<{ success: boolean }> => {
      return this.request(`/bom/assembly-items/${id}`, {
        method: 'DELETE',
      });
    },
  };

  // Bins
  bins = {
    getAll: async (params: { status?: string; workstationType?: string } = {}): Promise<any[]> => {
      const searchParams = new URLSearchParams();
      if (params.status) searchParams.set('status', params.status);
      if (params.workstationType) searchParams.set('workstationType', params.workstationType);
      return this.request(`/bins?${searchParams.toString()}`);
    },

    getAvailable: async (workstationType?: string): Promise<any[]> => {
      const params = workstationType ? `?workstationType=${workstationType}` : '';
      return this.request(`/bins/available${params}`);
    },

    getStats: async (): Promise<{
      total: number;
      available: number;
      assigned: number;
      inUse: number;
      completed: number;
    }> => {
      return this.request('/bins/stats');
    },

    getById: async (id: string): Promise<any> => {
      return this.request(`/bins/${id}`);
    },

    create: async (data: {
      binNumber: string;
      nickname?: string;
      location?: string;
      workstationType?: string;
    }): Promise<any> => {
      return this.request('/bins', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update: async (id: string, data: {
      nickname?: string;
      location?: string;
      workstationType?: string;
      status?: string;
    }): Promise<any> => {
      return this.request(`/bins/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    delete: async (id: string): Promise<void> => {
      return this.request(`/bins/${id}`, {
        method: 'DELETE',
      });
    },

    assign: async (id: string, data: {
      orderId: string;
      workOrderId: string;
      orderNumber: string;
      assignedBy: string;
    }): Promise<any> => {
      return this.request(`/bins/${id}/assign`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    release: async (id: string): Promise<any> => {
      return this.request(`/bins/${id}/release`, {
        method: 'POST',
      });
    },

    scan: async (barcode: string): Promise<{ type: string; data: any }> => {
      return this.request('/bins/scan', {
        method: 'POST',
        body: JSON.stringify({ barcode }),
      });
    },

    bulkCreate: async (data: {
      prefix: string;
      startNumber: number;
      count: number;
      location?: string;
      workstationType?: string;
    }): Promise<any[]> => {
      return this.request('/bins/bulk-create', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // Label printing methods
    printLabel: async (id: string): Promise<{ binNumber: string; zpl: string; format: string }> => {
      return this.request(`/bins/${id}/print-label`, {
        method: 'POST',
        body: JSON.stringify({ markPrinted: true }),
      });
    },

    printLabelsBatch: async (binIds: string[]): Promise<{ count: number; labels: { binNumber: string; zpl: string }[] }> => {
      return this.request('/bins/print-labels/batch', {
        method: 'POST',
        body: JSON.stringify({ binIds, markPrinted: true }),
      });
    },

    getBinsNeedingLabels: async (): Promise<any[]> => {
      return this.request('/bins/labels/needed');
    },

    // Bin contents methods
    getContents: async (id: string): Promise<any[]> => {
      return this.request(`/bins/${id}/contents`);
    },

    scanIn: async (binBarcode: string, partBarcode: string, data?: { quantity?: number; notes?: string }): Promise<any> => {
      return this.request('/bins/scan-in', {
        method: 'POST',
        body: JSON.stringify({ binBarcode, partBarcode, ...data }),
      });
    },

    scanOut: async (binBarcode: string, partBarcode: string): Promise<any> => {
      return this.request('/bins/scan-out', {
        method: 'POST',
        body: JSON.stringify({ binBarcode, partBarcode }),
      });
    },

    findPart: async (partNumber: string): Promise<any[]> => {
      return this.request(`/bins/find-part/${encodeURIComponent(partNumber)}`);
    },
  };

  // Labels
  labels = {
    // Queue a label for printing
    queueLabel: async (data: {
      workOrderId: string;
      labelType: string;
      copies?: number;
      workstationId?: string;
      customData?: Record<string, any>;
    }): Promise<any> => {
      return this.request('/labels/queue', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // Get labels in queue
    getQueue: async (filter?: {
      status?: string;
      labelType?: string;
      workstationId?: string;
      limit?: number;
    }): Promise<any[]> => {
      const params = new URLSearchParams();
      if (filter?.status) params.append('status', filter.status);
      if (filter?.labelType) params.append('labelType', filter.labelType);
      if (filter?.workstationId) params.append('workstationId', filter.workstationId);
      if (filter?.limit) params.append('limit', filter.limit.toString());
      return this.request(`/labels/queue?${params.toString()}`);
    },

    // Mark label as printed
    markPrinted: async (labelId: string, printer: string): Promise<any> => {
      return this.request(`/labels/${labelId}/print`, {
        method: 'POST',
        body: JSON.stringify({ printer }),
      });
    },

    // Mark label as error
    markError: async (labelId: string, error: string): Promise<any> => {
      return this.request(`/labels/${labelId}/error`, {
        method: 'POST',
        body: JSON.stringify({ error }),
      });
    },

    // Requeue a failed label
    requeueLabel: async (labelId: string): Promise<any> => {
      return this.request(`/labels/${labelId}/requeue`, { method: 'POST' });
    },

    // Get ZPL for a label
    getZpl: async (labelId: string): Promise<{ zpl: string }> => {
      return this.request(`/labels/${labelId}/zpl`);
    },

    // Get templates
    getTemplates: async (labelType?: string): Promise<any[]> => {
      const params = labelType ? `?labelType=${labelType}` : '';
      return this.request(`/labels/templates${params}`);
    },

    // Create template
    createTemplate: async (data: {
      name: string;
      labelType: string;
      zplTemplate: string;
      widthInches?: number;
      heightInches?: number;
      isDefault?: boolean;
      designerData?: any;
    }): Promise<any> => {
      return this.request('/labels/templates', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // Get single template
    getTemplate: async (id: string): Promise<any> => {
      return this.request(`/labels/templates/${id}`);
    },

    // Update template
    updateTemplate: async (id: string, data: any): Promise<any> => {
      return this.request(`/labels/templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    // Delete template
    deleteTemplate: async (id: string): Promise<any> => {
      return this.request(`/labels/templates/${id}`, {
        method: 'DELETE',
      });
    },

    // Get printers
    getPrinters: async (workstationId?: string): Promise<any[]> => {
      const params = workstationId ? `?workstationId=${workstationId}` : '';
      return this.request(`/labels/printers${params}`);
    },

    // Create printer
    createPrinter: async (data: { name: string; ipAddress: string; port?: number; workstationId?: string; isDefault?: boolean }): Promise<any> => {
      return this.request('/labels/printers', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    // Update printer
    updatePrinter: async (id: string, data: any): Promise<any> => {
      return this.request(`/labels/printers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    // Delete printer
    deletePrinter: async (id: string): Promise<any> => {
      return this.request(`/labels/printers/${id}`, {
        method: 'DELETE',
      });
    },

    // Test printer connection
    testPrinter: async (id: string): Promise<{ success: boolean; message: string }> => {
      return this.request(`/labels/printers/${id}/test`, {
        method: 'POST',
      });
    },

    // Generate bin label
    generateBinLabel: async (binId: string, labelSize?: string): Promise<{ binNumber: string; location?: string; zpl: string }> => {
      return this.request(`/bins/${binId}/print-label`, {
        method: 'POST',
        body: JSON.stringify({ markPrinted: true, labelSize: labelSize || '3x1' }),
      });
    },

    // Generate part label
    generatePartLabel: async (partNumber: string, description?: string, quantity?: number): Promise<{ partNumber: string; zpl: string }> => {
      return this.request('/bins/labels/part', {
        method: 'POST',
        body: JSON.stringify({ partNumber, description, quantity }),
      });
    },

    // Generate drop label
    generateDropLabel: async (dropId: string, partType: string, length: number, partNumber?: string, color?: string, height?: number, width?: number): Promise<{ dropId: string; zpl: string }> => {
      return this.request('/bins/labels/drop', {
        method: 'POST',
        body: JSON.stringify({ dropId, partType, length, partNumber, color, height, width }),
      });
    },

    // Batch print bin labels
    printBinLabelsBatch: async (binIds: string[], labelSize?: string): Promise<{ count: number; labels: { binNumber: string; zpl: string }[] }> => {
      return this.request('/bins/print-labels/batch', {
        method: 'POST',
        body: JSON.stringify({ binIds, markPrinted: true, labelSize: labelSize || '3x1' }),
      });
    },

    // Get bins needing labels
    getBinsNeedingLabels: async (): Promise<any[]> => {
      return this.request('/bins/labels/needed');
    },

    // Queue labels for all work orders in an order
    queueOrderLabels: async (orderId: string, labelType: string, copies: number = 1): Promise<any[]> => {
      return this.request('/labels/queue-order', {
        method: 'POST',
        body: JSON.stringify({ orderId, labelType, copies }),
      });
    },

    // Send label directly to printer via TCP
    printDirect: async (labelId: string, printerId: string): Promise<{ success: boolean; message: string }> => {
      return this.request(`/labels/${labelId}/print-direct`, {
        method: 'POST',
        body: JSON.stringify({ printerId }),
      });
    },
  };

  // Legacy method for compatibility
  async generateLabel(workOrderId: string, labelType: string): Promise<Blob> {
    const response = await fetch(`${API_BASE}/labels/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify({ workOrderId, labelType }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate label');
    }

    return response.blob();
  }

  // Reports
  async getProductionReport(startDate: string, endDate: string): Promise<any> {
    return this.request(`/reports/production?startDate=${startDate}&endDate=${endDate}`);
  }

  async getSalesReport(startDate: string, endDate: string): Promise<any> {
    return this.request(`/reports/sales?startDate=${startDate}&endDate=${endDate}`);
  }

  async getInventoryReport(): Promise<any> {
    return this.request('/reports/inventory');
  }

  async getDealerReport(dealerId?: string): Promise<any> {
    const params = dealerId ? `?dealerId=${dealerId}` : '';
    return this.request(`/reports/dealers${params}`);
  }

  // BOM
  bom = {
    // Get BOM for an order
    getBom: async (orderId: string): Promise<any> => {
      return this.request(`/bom/${orderId}`);
    },

    // Generate BOM preview (doesn't save)
    generateBomPreview: async (orderId: string): Promise<any[]> => {
      return this.request(`/bom/generate/${orderId}`, { method: 'POST' });
    },

    // TEMPORARY — every BOM engine's numbers for one order, side by side.
    // Remove with the BOM Compare tab once the engines are consolidated.

    // Get cut sheet for an order
    getCutSheet: async (orderId: string): Promise<any> => {
      return this.request(`/bom/cut-sheet/${orderId}`);
    },

    // Check inventory availability
    checkAvailability: async (orderId: string): Promise<{
      available: boolean;
      shortages: any[];
      warnings: string[];
    }> => {
      return this.request(`/bom/availability/${orderId}`);
    },

    // Allocate inventory for order
    allocateInventory: async (orderId: string): Promise<any> => {
      return this.request(`/bom/allocate/${orderId}`, { method: 'POST' });
    },

    // Release allocated inventory
    releaseInventory: async (orderId: string): Promise<any> => {
      return this.request(`/bom/release/${orderId}`, { method: 'POST' });
    },

    // Calculate cuts from dimensions
    calculateCuts: async (dimensions: {
      widthTop: number;
      widthMiddle: number;
      widthBottom: number;
      heightLeft: number;
      heightCenter: number;
      heightRight: number;
      uChannelLeft?: boolean;
      uChannelRight?: boolean;
      boxMount?: string;
      motorType?: string;
      brushSize?: string;
      fabricType?: string;
    }): Promise<any> => {
      return this.request('/bom/calculate-cuts', {
        method: 'POST',
        body: JSON.stringify(dimensions),
      });
    },

    // Calculate spring hole positions
    calculateSpringHoles: async (systemHeight: number): Promise<{
      holes: number;
      spacing: number;
      positions: number[];
    }> => {
      return this.request('/bom/spring-holes', {
        method: 'POST',
        body: JSON.stringify({ systemHeight }),
      });
    },

    // Calculate full BOM with cut lengths and costs
    // Costs a grid of sizes with the same server engine as calculateFullBom.
    calculateBomGrid: async (params: {
      widths: number[];
      heights: number[];
      motorType?: string;
      brushSize?: string;
      fabricType?: string;
      fabricColor?: string;
      systemColor?: string;
      systemType?: string;
      uChannelLeft?: boolean;
      uChannelRight?: boolean;
      applyUChannelDeductions?: boolean;
      applyBoxDeduction?: boolean;
    }): Promise<{ results: Array<{ width: number; height: number; totalBomCost: number; itemCount: number }> }> => {
      return this.request('/bom/calculate-bom-grid', {
        method: 'POST',
        body: JSON.stringify(params),
      });
    },

    calculateFullBom: async (dimensions: {
      widthTop: number;
      widthMiddle: number;
      widthBottom: number;
      heightLeft: number;
      heightCenter: number;
      heightRight: number;
      uChannelLeft?: boolean;
      uChannelRight?: boolean;
      boxMount?: string;
      motorType?: string;
      brushSize?: string;
      fabricType?: string;
      fabricColor?: string;
      systemColor?: string;
      trackMountLeft?: string;
      trackMountRight?: string;
      applyUChannelDeductions?: boolean;
      applyBoxDeduction?: boolean;
      boxWidthAdjustment?: number;
      systemType?: string;
    }): Promise<{
      dimensions: any;
      cutSizes: any;
      springHoles: any;
      seamRequired: boolean;
      warnings: string[];
      bomItems: Array<{
        partNumber: string;
        description: string;
        quantity: number;
        cutLength?: number;
        stockLength?: number;
        unitCost?: number;
        proratedCost?: number;
        totalCost?: number;
        inventoryItemId?: string;
        componentType?: string;
        stockTakeType?: string;
        isExtrusion?: boolean;
        quantityFormula?: string;
        inventoryGroup?: string;
      }>;
      summary: {
        totalItems: number;
        extrusionItems: number;
        totalBomCost: number;
        totalExtrusionCost: number;
      };
    }> => {
      return this.request('/bom/calculate-bom', {
        method: 'POST',
        body: JSON.stringify(dimensions),
      });
    },
  };

  // HR - Time Off
  async getTimeOffRequests(params: { status?: string } = {}): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set('status', params.status);
    return this.request(`/time-off/admin/requests?${searchParams.toString()}`);
  }

  async approveTimeOffRequest(id: string): Promise<any> {
    return this.request(`/time-off/admin/approve/${id}`, {
      method: 'POST',
    });
  }

  async denyTimeOffRequest(id: string, reason: string): Promise<any> {
    return this.request(`/time-off/admin/deny/${id}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // HR - Employee Shifts
  async getShiftsForDate(date: string): Promise<any[]> {
    return this.request(`/employee-shifts/date/${date}`);
  }

  // HR - Training
  async getTrainingModules(): Promise<any[]> {
    return this.request('/training/modules');
  }

  async getTrainingCompletionReport(): Promise<any[]> {
    return this.request('/training/admin/completion-report');
  }

  async createTrainingModule(data: any): Promise<any> {
    return this.request('/training/modules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTrainingModule(id: string, data: any): Promise<any> {
    return this.request(`/training/modules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTrainingModule(id: string): Promise<void> {
    return this.request(`/training/modules/${id}`, {
      method: 'DELETE',
    });
  }

  // Dealer Training Management
  async getDealerTrainingModules(): Promise<any[]> {
    return this.request('/rewards/admin/training-modules');
  }

  async createDealerTrainingModule(data: any): Promise<any> {
    return this.request('/rewards/admin/training-modules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDealerTrainingModule(id: string, data: any): Promise<any> {
    return this.request(`/rewards/admin/training-modules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getModuleQuestions(moduleId: string): Promise<any[]> {
    return this.request(`/rewards/admin/training-modules/${moduleId}/questions`);
  }

  async createModuleQuestion(moduleId: string, data: any): Promise<any> {
    return this.request(`/rewards/admin/training-modules/${moduleId}/questions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateModuleQuestion(questionId: string, data: any): Promise<any> {
    return this.request(`/rewards/admin/questions/${questionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteModuleQuestion(questionId: string): Promise<any> {
    return this.request(`/rewards/admin/questions/${questionId}`, {
      method: 'DELETE',
    });
  }

  // HR - Documents
  async getAllDocuments(): Promise<any[]> {
    return this.request('/sop-documents');
  }

  async getDocumentCategories(): Promise<string[]> {
    return this.request('/sop-documents/categories');
  }

  async createDocument(data: any): Promise<any> {
    return this.request('/sop-documents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDocument(id: string, data: any): Promise<any> {
    return this.request(`/sop-documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteDocument(id: string): Promise<void> {
    return this.request(`/sop-documents/${id}`, {
      method: 'DELETE',
    });
  }

  // Real file upload (multipart). Do NOT set Content-Type — the browser sets the
  // multipart boundary. Fields: file, title, category, description, version, audience.
  async uploadDocument(formData: FormData): Promise<any> {
    const token = this.token || localStorage.getItem('admin_auth_token');
    const res = await fetch(`${API_BASE}/sop-documents/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(Array.isArray(err.message) ? err.message.join('; ') : err.message || 'Upload failed');
    }
    return res.json();
  }

  // Preview/download an uploaded file. The endpoint requires auth, so this can't
  // be a plain link: fetch with the token, then open the blob.
  async openDocumentFile(id: string, download = false): Promise<void> {
    const token = this.token || localStorage.getItem('admin_auth_token');
    const res = await fetch(`${API_BASE}/sop-documents/${id}/file${download ? '?download=1' : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: `Couldn't open the file (${res.status})` }));
      throw new Error(Array.isArray(err.message) ? err.message.join('; ') : err.message);
    }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  // HR - Rewards
  async getRewardsCatalog(): Promise<any[]> {
    return this.request('/employee-rewards/catalog');
  }

  async createReward(data: any): Promise<any> {
    return this.request('/employee-rewards/catalog', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReward(id: string, data: any): Promise<any> {
    return this.request(`/employee-rewards/catalog/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getAllRedemptions(params: { status?: string } = {}): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set('status', params.status);
    return this.request(`/employee-rewards/admin/redemptions?${searchParams.toString()}`);
  }

  async getRewardsLeaderboard(params: { limit?: number } = {}): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', params.limit.toString());
    return this.request(`/employee-rewards/leaderboard?${searchParams.toString()}`);
  }

  async fulfillRedemption(id: string, notes: string): Promise<any> {
    return this.request(`/employee-rewards/admin/fulfill/${id}`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async cancelRedemption(id: string): Promise<any> {
    return this.request(`/employee-rewards/admin/cancel/${id}`, {
      method: 'POST',
    });
  }

  async awardBonusPoints(data: { employee_id: string; points: number; reason: string }): Promise<any> {
    return this.request('/employee-rewards/admin/award-points', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // HR - Employees
  async getAllEmployees(): Promise<any[]> {
    return this.request('/employees');
  }
}

const apiInstance = new ApiService();

// Create namespaced API object for convenient access
export const api = {
  setToken: (token: string | null) => apiInstance.setToken(token),
  login: (email: string, password: string) => apiInstance.login(email, password),
  getProfile: () => apiInstance.getProfile(),

  // Generic methods for endpoints without specific wrappers
  get: async <T = any>(endpoint: string): Promise<T> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const err: any = new Error(error.message || `HTTP ${response.status}`);
      err.response = { data: error };
      throw err;
    }
    return response.json();
  },
  post: async <T = any>(endpoint: string, data: any): Promise<{ data: T }> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const err: any = new Error(error.message || `HTTP ${response.status}`);
      err.response = { data: error };
      throw err;
    }
    const result = await response.json();
    return { data: result };
  },
  patch: async <T = any>(endpoint: string, data: any): Promise<T> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const err: any = new Error(error.message || `HTTP ${response.status}`);
      err.response = { data: error };
      throw err;
    }
    return response.json();
  },
  del: async (endpoint: string): Promise<any> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const err: any = new Error(error.message || `HTTP ${response.status}`);
      err.response = { data: error };
      throw err;
    }
    return response.json();
  },
  getDashboardStats: () => apiInstance.getDashboardStats(),
  getOrderStatusCounts: () => apiInstance.getOrderStatusCounts(),
  getRecentActivity: (limit?: number) => apiInstance.getRecentActivity(limit),
  getOrderLaborBom: (orderId: string) => apiInstance.getOrderLaborBom(orderId),
  clearInvoiceCorrection: (id: string) => apiInstance.clearInvoiceCorrection(id),

  // Orders namespace
  orders: {
    getOrders: (params?: Parameters<typeof apiInstance.getOrders>[0]) => apiInstance.getOrders(params),
    getOrder: (id: string) => apiInstance.getOrder(id),
    updateOrder: (id: string, data: Record<string, any>) => apiInstance.updateOrder(id, data),
    updateRush: (id: string, data: { isRush?: boolean; rushSurchargeWaived?: boolean }) => apiInstance.updateRush(id, data),
    voidParcelLabel: (id: string) => apiInstance.voidParcelLabel(id),
    setShippingMethod: (id: string, method: 'shipping' | 'will_call') => apiInstance.setShippingMethod(id, method),
    syncFreightTracking: (orderId: string) => apiInstance.syncFreightTracking(orderId),
    updateShippingTracking: (orderId: string, body: { carrier?: string; bolNumber?: string; proNumber?: string; shippedAt?: string }) =>
      apiInstance.updateShippingTracking(orderId, body),
    updateOrderStatus: (id: string, status: OrderStatus, notes?: string) => apiInstance.updateOrderStatus(id, status, notes),
    rejectForCorrection: (id: string, reason: string) => apiInstance.rejectForCorrection(id, reason),
    releaseOrder: (id: string) => apiInstance.releaseOrder(id),
    pushToProduction: (id: string) => apiInstance.pushToProduction(id),
    updateSchedule: (id: string, estimatedShipDate: string) => apiInstance.updateSchedule(id, estimatedShipDate),
    clearInvoiceCorrection: (id: string) => apiInstance.clearInvoiceCorrection(id),
    addOrderNote: (id: string, note: string, isInternal?: boolean) => apiInstance.addOrderNote(id, note, isInternal),
    approveWithoutDeposit: (id: string) => apiInstance.approveWithoutDeposit(id),
    recordPayment: (id: string, data: { amount: number; payment_type: string; payment_method: string; payment_reference?: string; notes?: string }) => apiInstance.recordPayment(id, data),
    adminEditOrder: (id: string, data: any) => apiInstance.adminEditOrder(id, data),
    adminEditItem: (orderId: string, itemId: string, data: any) => apiInstance.adminEditItem(orderId, itemId, data),
    getInvoice: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/invoice`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch invoice');
      return response.json();
    },
    saveInvoice: async (id: string, invoiceData: any): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/invoice`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ invoiceData }),
      });
      if (!response.ok) throw new Error('Failed to save invoice');
      return response.json();
    },
    sendInvoice: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/send-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to send invoice');
      return response.json();
    },
    writeOff: async (id: string, reason: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/write-off`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reason }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to write off order');
      }
      return response.json();
    },
    createShippingAdjustment: async (id: string, actualShippingAmount: number, notes?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/shipping-adjustment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ actualShippingAmount, notes }),
      });
      if (!response.ok) throw new Error('Failed to create shipping adjustment');
      return response.json();
    },
    setSupportCommission: async (id: string, body: { supportRepId?: string | null; supportCommissionRate?: number | null; supportCommissionAmount?: number | null; supportCommissionNotes?: string | null }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/support-commission`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to set support commission');
      }
      return response.json();
    },
    issueRefund: async (
      id: string,
      body: { amount: number; method: 'check'|'ach'|'cash'|'wire'|'credit_to_account'|'other'; reference?: string; reason?: string },
    ): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to issue refund');
      }
      return response.json();
    },
    listRefunds: async (id: string): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/refunds`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to load refunds');
      return response.json();
    },
    // Admin edit of a parts order (qty/color/length per item).
    // Backend route: PATCH /orders/:id/parts (admin path bypasses status
    // restriction + dealer ownership when JWT role is admin/employee).
    updateParts: async (id: string, body: { items: any[]; subtotal: number; total: number; sidemark?: string | null; poNumber?: string | null; shipping?: number; shippingMethod?: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/parts`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update parts order');
      }
      return response.json();
    },
    markSentToQuickbooks: async (id: string, sent: boolean): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/quickbooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ sent }),
      });
      if (!response.ok) throw new Error('Failed to update QuickBooks status');
      return response.json();
    },
    acknowledgeInvoiceChange: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/acknowledge-invoice-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to acknowledge invoice change');
      return response.json();
    },
    archiveOrder: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/archive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to archive order');
      return response.json();
    },
    unarchiveOrder: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}/unarchive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to unarchive order');
      return response.json();
    },
    deleteOrder: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/orders/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to delete order');
      }
      return response.json();
    },
  },

  // Production namespace
  production: {
    getWorkstationQueues: () => apiInstance.getWorkstationQueues(),
    getOrdersInProduction: () => apiInstance.getOrdersInProduction(),
    scheduleOrder: async (orderId: string, scheduledDate: string | null, priority?: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/production/calendar/${orderId}/schedule`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ scheduledDate, priority }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to schedule order');
      }
      return response.json();
    },
    getWorkstationQueue: (workstation: WorkstationStep) => apiInstance.getWorkstationQueue(workstation),
    getWorkOrders: (params?: Parameters<typeof apiInstance.getWorkOrders>[0]) => apiInstance.getWorkOrders(params),
    getWorkOrder: (id: string) => apiInstance.getWorkOrder(id),
    updateWorkOrderStatus: (id: string, status: WorkOrderStatus) => apiInstance.updateWorkOrderStatus(id, status),
    blockWorkOrder: (id: string, blockerType: string, reason: string) => apiInstance.blockWorkOrder(id, blockerType, reason),
    unblockWorkOrder: (id: string) => apiInstance.unblockWorkOrder(id),
    completeWorkstationStep: (workOrderId: string, workstation: WorkstationStep) => apiInstance.completeWorkstationStep(workOrderId, workstation),

    // Workstation Management
    getAllWorkstations: async (includeInactive = true): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/production/workstations-all?includeInactive=${includeInactive}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch workstations');
      return response.json();
    },
    getDependencies: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/production/dependencies`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch dependencies');
      return response.json();
    },
    updateWorkstation: async (id: string, data: { name?: string; sequence?: number; is_active?: boolean; active?: boolean; portal?: string; expectedMinutesPerUnit?: number | null }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/production/workstations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update workstation');
      return response.json();
    },
    setDependencies: async (id: string, dependsOnIds: string[]): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/production/dependencies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ dependsOnIds }),
      });
      if (!response.ok) throw new Error('Failed to set dependencies');
      return response.json();
    },

    // Fabric Needs
    getFabricNeeds: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/inventory/fabric-needs`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch fabric needs');
      return response.json();
    },

    // Shortage Report
    getShortageReport: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/production/shortage-report`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch shortage report');
      return response.json();
    },
    getShortageSummary: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/production/pipeline/shortage-summary`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch shortage summary');
      return response.json();
    },
    resolveShortage: async (orderId: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/production/shortage/${orderId}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to resolve shortage');
      return response.json();
    },
  },

  // Inventory
  inventory: apiInstance.inventory,

  // Bins
  bins: apiInstance.bins,

  // BOM
  bom: apiInstance.bom,

  // Labels
  labels: apiInstance.labels,
  generateLabel: (workOrderId: string, labelType: string) => apiInstance.generateLabel(workOrderId, labelType),

  // Reports
  reports: {
    getProductionReport: (params: { startDate: string; endDate: string }) => apiInstance.getProductionReport(params.startDate, params.endDate),
    getSalesReport: (params: { startDate: string; endDate: string }) => apiInstance.getSalesReport(params.startDate, params.endDate),
    getInventoryReport: () => apiInstance.getInventoryReport(),
    getDealerReport: (params: { startDate: string; endDate: string }) => apiInstance.getDealerReport(),
    // New Inventory Reports
    getInventoryConsumptionReport: async (startDate: string, endDate: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/reports/inventory-consumption?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      return response.json();
    },
    getDropUtilizationReport: async (startDate: string, endDate: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/reports/drop-utilization?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      return response.json();
    },
    getWasteTrackingReport: async (startDate: string, endDate: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/reports/waste-tracking?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      return response.json();
    },
    getPurchasingReport: async (purchasingGroup?: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const params = purchasingGroup ? `?purchasingGroup=${purchasingGroup}` : '';
      const response = await fetch(`${API_BASE}/reports/purchasing${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      return response.json();
    },
    getRevenueForecastReport: async (days: number = 90) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/reports/revenue-forecast?days=${days}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      return response.json();
    },
    getExtrusionPOReport: async (params: { lookbackDays?: number; leadTimeDays?: number; targetCoverageDays?: number; safetyStockPct?: number } = {}) => {
      const token = localStorage.getItem('admin_auth_token');
      const qs = new URLSearchParams();
      if (params.lookbackDays) qs.set('lookbackDays', String(params.lookbackDays));
      if (params.leadTimeDays) qs.set('leadTimeDays', String(params.leadTimeDays));
      if (params.targetCoverageDays) qs.set('targetCoverageDays', String(params.targetCoverageDays));
      if (params.safetyStockPct !== undefined) qs.set('safetyStockPct', String(params.safetyStockPct));
      const response = await fetch(`${API_BASE}/reports/extrusion-po?${qs.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      return response.json();
    },
    getExtrusionPOPdf: async (params: { lookbackDays?: number; leadTimeDays?: number; targetCoverageDays?: number; safetyStockPct?: number } = {}) => {
      const token = localStorage.getItem('admin_auth_token');
      const qs = new URLSearchParams();
      if (params.lookbackDays) qs.set('lookbackDays', String(params.lookbackDays));
      if (params.leadTimeDays) qs.set('leadTimeDays', String(params.leadTimeDays));
      if (params.targetCoverageDays) qs.set('targetCoverageDays', String(params.targetCoverageDays));
      if (params.safetyStockPct !== undefined) qs.set('safetyStockPct', String(params.safetyStockPct));
      const response = await fetch(`${API_BASE}/reports/extrusion-po/pdf?${qs.toString()}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      return response.blob();
    },
    getLostQuotesReport: async (startDate: string, endDate: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/reports/lost-quotes?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      return response.json();
    },
    getCogsReport: async (startDate: string, endDate: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/reports/cogs?startDate=${startDate}&endDate=${endDate}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      return response.json();
    },
  },

  // Fabric Settings
  fabricSettings: {
    getAll: async (category?: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const params = category ? `?category=${category}` : '';
      const response = await fetch(`${API_BASE}/fabric-settings${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch fabric settings');
      return response.json();
    },
    getCategories: async () => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/categories`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch categories');
      return response.json();
    },
    getByFabricType: async (fabricType: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/${encodeURIComponent(fabricType)}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch fabric settings');
      return response.json();
    },
    update: async (fabricType: string, data: any) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/${encodeURIComponent(fabricType)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update fabric settings');
      return response.json();
    },
    create: async (data: any) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create fabric settings');
      return response.json();
    },
    getCatalog: async () => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/catalog`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch fabric catalog');
      return response.json();
    },
    createType: async (data: any) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create fabric type');
      return response.json();
    },
    updateType: async (id: string, data: any) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update fabric type');
      return response.json();
    },
    createColor: async (fabricTypeId: string, data: any) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/types/${fabricTypeId}/colors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create fabric color');
      return response.json();
    },
    updateColor: async (id: string, data: any) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/colors/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update fabric color');
      return response.json();
    },
    deleteColor: async (id: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/fabric-settings/colors/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to delete fabric color');
    },
  },

  // System Settings
  systemSettings: {
    getPricing: async () => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/system-settings/pricing`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch pricing settings');
      return response.json();
    },
    setPriceIncrease: async (value: number) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/system-settings/price-increase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ value }),
      });
      if (!response.ok) throw new Error('Failed to update price increase');
      return response.json();
    },
    setAchDiscount: async (value: number) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/system-settings/ach-discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ value }),
      });
      if (!response.ok) throw new Error('Failed to update ACH discount');
      return response.json();
    },
    getLeadTimes: async () => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/system-settings/lead-times`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch lead times');
      return response.json();
    },
    setLeadTimes: async (leadTimes: { solar?: number; premiumSolar?: number; cvw?: number; insect?: number }) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/system-settings/lead-times`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(leadTimes),
      });
      if (!response.ok) throw new Error('Failed to update lead times');
      return response.json();
    },
    getPowderCoatCharge: async () => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/system-settings/powder-coat-charge`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch powder coat charge');
      return response.json();
    },
    setPowderCoatCharge: async (value: number) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/system-settings/powder-coat-charge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ value }),
      });
      if (!response.ok) throw new Error('Failed to update powder coat charge');
      return response.json();
    },
  },

  // HR - Time Off
  getTimeOffRequests: (params?: { status?: string }) => apiInstance.getTimeOffRequests(params),
  approveTimeOffRequest: (id: string) => apiInstance.approveTimeOffRequest(id),
  denyTimeOffRequest: (id: string, reason: string) => apiInstance.denyTimeOffRequest(id, reason),

  // HR - Employee Shifts
  getShiftsForDate: (date: string) => apiInstance.getShiftsForDate(date),

  // HR - Training (Employee)
  getTrainingModules: () => apiInstance.getTrainingModules(),
  getTrainingCompletionReport: () => apiInstance.getTrainingCompletionReport(),
  createTrainingModule: (data: any) => apiInstance.createTrainingModule(data),
  updateTrainingModule: (id: string, data: any) => apiInstance.updateTrainingModule(id, data),
  deleteTrainingModule: (id: string) => apiInstance.deleteTrainingModule(id),

  // Dealer Training Management
  getDealerTrainingModules: () => apiInstance.getDealerTrainingModules(),
  createDealerTrainingModule: (data: any) => apiInstance.createDealerTrainingModule(data),
  updateDealerTrainingModule: (id: string, data: any) => apiInstance.updateDealerTrainingModule(id, data),
  getModuleQuestions: (moduleId: string) => apiInstance.getModuleQuestions(moduleId),
  createModuleQuestion: (moduleId: string, data: any) => apiInstance.createModuleQuestion(moduleId, data),
  updateModuleQuestion: (questionId: string, data: any) => apiInstance.updateModuleQuestion(questionId, data),
  deleteModuleQuestion: (questionId: string) => apiInstance.deleteModuleQuestion(questionId),

  // HR - Documents
  getAllDocuments: () => apiInstance.getAllDocuments(),
  getDocumentCategories: () => apiInstance.getDocumentCategories(),
  createDocument: (data: any) => apiInstance.createDocument(data),
  updateDocument: (id: string, data: any) => apiInstance.updateDocument(id, data),
  deleteDocument: (id: string) => apiInstance.deleteDocument(id),

  // HR - Rewards
  getRewardsCatalog: () => apiInstance.getRewardsCatalog(),
  createReward: (data: any) => apiInstance.createReward(data),
  updateReward: (id: string, data: any) => apiInstance.updateReward(id, data),
  getAllRedemptions: (params?: { status?: string }) => apiInstance.getAllRedemptions(params),
  getRewardsLeaderboard: (params?: { limit?: number }) => apiInstance.getRewardsLeaderboard(params),
  fulfillRedemption: (id: string, notes: string) => apiInstance.fulfillRedemption(id, notes),
  cancelRedemption: (id: string) => apiInstance.cancelRedemption(id),
  awardBonusPoints: (data: { employee_id: string; points: number; reason: string }) => apiInstance.awardBonusPoints(data),

  // HR - Employees
  getAllEmployees: () => apiInstance.getAllEmployees(),

  // Accounting Section
  accounting: {
    // Dashboard
    getDashboard: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/dashboard`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch dashboard stats');
      return response.json();
    },

    // Dealer Credits & Overpayments
    getCreditsSummary: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/credits/summary`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch credits summary');
      return response.json();
    },
    getDealerCredits: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/credits/dealers`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch dealer credits');
      return response.json();
    },
    getOpenOverpayments: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/credits/overpayments`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch open overpayments');
      return response.json();
    },
    applyCreditToOrder: async (dealerId: string, orderId: string, amount: number): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/credits/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ dealerId, orderId, amount }),
      });
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.message || 'Failed to apply credit');
      }
      return response.json();
    },
    // Manual store-credit adjustment (add/subtract a dealer's balance).
    adjustDealerCredit: async (dealerId: string, amount: number, description: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/dealer-credit/admin/${dealerId}/adjustment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ amount, description }),
      });
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.message || 'Failed to adjust credit');
      }
      return response.json();
    },

    // Pending Dealer Applications (API uses pending-applications)
    getPendingApplications: async (params?: { status?: string; search?: string; page?: number; limit?: number }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.search) searchParams.set('search', params.search);
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const response = await fetch(`${API_BASE}/accounting/pending-applications?${searchParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch pending applications');
      return response.json();
    },

    approveInterest: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/dealer-applications/${id}/approve-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success === false) throw new Error(data?.message || 'Failed to approve interest');
      return data;
    },

    denyInterest: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/dealer-applications/${id}/deny-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success === false) throw new Error(data?.message || 'Failed to deny interest');
      return data;
    },

    resendApplication: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/dealer-applications/${id}/resend-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success === false) throw new Error(data?.message || 'Failed to resend application');
      return data;
    },

    getWriteOffs: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/write-offs`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch write-offs');
      return response.json();
    },

    setBomWrittenOff: async (orderId: string, value: boolean): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/write-offs/${orderId}/bom-written-off`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ value }),
      });
      if (!response.ok) throw new Error('Failed to update BOM write-off');
      return response.json();
    },

    getPendingApplication: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch pending application');
      return response.json();
    },

    updatePendingApplication: async (id: string, data: any): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update application');
      return response.json();
    },

    approveDealerApplication: async (id: string, data: { notes?: string; discount_rate?: number; sales_rep_id?: string; account_type?: string; territory_id?: string; lead_source?: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to approve application');
      return response.json();
    },

    grantPortalAccess: async (id: string, data: { discount_rate?: number; sales_rep_id?: string; account_type?: string; territory_id?: string; lead_source?: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}/grant-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to grant portal access');
      }
      return response.json();
    },

    rejectDealerApplication: async (id: string, data: { rejection_reason: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to reject application');
      return response.json();
    },

    requestInfoDealerApplication: async (id: string, data: { info_request_message: string; flagged_items?: string[] }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}/request-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to request info');
      return response.json();
    },

    // Alias for backward compatibility
    requestMoreInfo: async (id: string, data: { info_request_message: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}/request-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to request info');
      return response.json();
    },

    dismissDealerApplication: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}/dismiss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to dismiss application');
      return response.json();
    },

    reopenDealerApplication: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}/reopen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to reopen application');
      return response.json();
    },

    deleteDealerApplication: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/pending-applications/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to delete application');
      return response.json();
    },

    // Bills
    getBills: async (params?: { month?: number; year?: number; category?: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const searchParams = new URLSearchParams();
      if (params?.month) searchParams.set('month', params.month.toString());
      if (params?.year) searchParams.set('year', params.year.toString());
      if (params?.category) searchParams.set('category', params.category);
      const response = await fetch(`${API_BASE}/accounting/bills?${searchParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch bills');
      return response.json();
    },

    getBill: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/bills/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch bill');
      return response.json();
    },

    createBill: async (data: {
      vendorName: string;
      invoiceNumber?: string;
      amount: number;
      dueDate: string;
      category: string;
      description?: string;
    }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/bills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create bill');
      return response.json();
    },

    updateBill: async (id: string, data: any): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/bills/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update bill');
      return response.json();
    },

    deleteBill: async (id: string): Promise<void> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/bills/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to delete bill');
    },

    // Expense Categories
    getExpenseCategories: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/expense-categories`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch expense categories');
      return response.json();
    },

    // Employees (limited tax info only)
    getEmployees: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/employees`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch employees');
      return response.json();
    },

    // Alias for backward compatibility
    getEmployeesForTax: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/employees`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch employees');
      return response.json();
    },

    getEmployeeTaxInfo: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/employees/${id}/tax-info`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch employee');
      return response.json();
    },

    updateEmployeeTaxInfo: async (id: string, data: {
      emergency_contact_name?: string;
      emergency_contact_phone?: string;
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
      filing_status?: string;
    }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/accounting/employees/${id}/tax-info`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update employee');
      return response.json();
    },

    // Revenue (read-only)
    getRevenueShipped: async (params: { month: number; year: number }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const searchParams = new URLSearchParams();
      searchParams.set('month', params.month.toString());
      searchParams.set('year', params.year.toString());
      const response = await fetch(`${API_BASE}/accounting/revenue/shipped?${searchParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch revenue');
      return response.json();
    },

    getRevenuePayments: async (params: { month: number; year: number }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const searchParams = new URLSearchParams();
      searchParams.set('month', params.month.toString());
      searchParams.set('year', params.year.toString());
      const response = await fetch(`${API_BASE}/accounting/revenue/payments?${searchParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch payments');
      return response.json();
    },

    // Activity Log
    getActivityLog: async (params?: { limit?: number }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      const response = await fetch(`${API_BASE}/accounting/activity?${searchParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch activity');
      return response.json();
    },
  },

  // Accounting Oversight (Admin visibility into accounting activity)
  accountingOversight: {
    getActivityLog: async (params?: { action_type?: string; entity_type?: string; limit?: number }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const searchParams = new URLSearchParams();
      if (params?.action_type) searchParams.set('action_type', params.action_type);
      if (params?.entity_type) searchParams.set('entity_type', params.entity_type);
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      const response = await fetch(`${API_BASE}/accounting/activity-log?${searchParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch activity log');
      return response.json();
    },

    getExpectedVsActual: async (params: { month: number; year: number }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const searchParams = new URLSearchParams();
      searchParams.set('month', params.month.toString());
      searchParams.set('year', params.year.toString());
      const response = await fetch(`${API_BASE}/accounting/expected-vs-actual?${searchParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch expected vs actual');
      return response.json();
    },
  },

  // Cost Accounting / Analytics
  costAccounting: {
    canView: async (): Promise<{ canView: boolean }> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/cost-accounting/can-view`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) return { canView: false };
      return response.json();
    },

    getSummary: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/summary?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch summary');
      return response.json();
    },

    getCostBreakdown: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/cost-breakdown?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch cost breakdown');
      return response.json();
    },

    getTrends: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/trends?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch trends');
      return response.json();
    },

    getProductionMetrics: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/production?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch production metrics');
      return response.json();
    },

    getProfitability: async (
      days: number = 30,
      startDate?: string,
      endDate?: string,
      sortBy: string = 'margin_percent',
      sortDir: 'asc' | 'desc' = 'desc',
      limit: number = 100,
      offset: number = 0
    ): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({
        days: days.toString(),
        sortBy,
        sortDir,
        limit: limit.toString(),
        offset: offset.toString(),
      });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/profitability?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch profitability');
      return response.json();
    },

    getBySize: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/by-size?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch size analysis');
      return response.json();
    },

    getByFabric: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/by-fabric?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch fabric analysis');
      return response.json();
    },

    getByWorkstation: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/by-workstation?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch workstation analysis');
      return response.json();
    },

    getByEmployee: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/by-employee?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch employee analysis');
      return response.json();
    },

    getReworkStats: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/rework?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch rework stats');
      return response.json();
    },

    getScrapStats: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/scrap?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch scrap stats');
      return response.json();
    },

    getInsights: async (days: number = 30, startDate?: string, endDate?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/insights?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch insights');
      return response.json();
    },

    exportCsv: async (days: number = 30, startDate?: string, endDate?: string): Promise<void> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = new URLSearchParams({ days: days.toString() });
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/export/csv?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to export CSV');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cost-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

    getOrderCostAnalysis: async (orderId: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/cost-accounting/order/${orderId}/cost-analysis`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch order cost analysis');
      return response.json();
    },

    recalculateCostSnapshots: async (orderId: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/cost-accounting/order/${orderId}/recalculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to recalculate cost snapshots');
      return response.json();
    },

    getOrderComparison: async (orderId: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/cost-accounting/analytics/order/${orderId}/comparison`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch order comparison');
      return response.json();
    },
  },

  // Company Events (Company Calendar)
  companyEvents: {
    getAllEvents: async (params?: { startDate?: string; endDate?: string; eventType?: string; status?: string }): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      if (params?.eventType) searchParams.set('eventType', params.eventType);
      if (params?.status) searchParams.set('status', params.status);
      const response = await fetch(`${API_BASE}/company-events/admin/all?${searchParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch events');
      return response.json();
    },

    getEvent: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/company-events/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch event');
      return response.json();
    },

    create: async (data: {
      title: string;
      description?: string;
      eventType: string;
      startTime: string;
      endTime?: string;
      allDay?: boolean;
      location?: string;
      virtualLink?: string;
      visibility?: string;
      dealerIds?: string[];
      employeeIds?: string[];
      department?: string | null;
      attendeeDealerId?: string | null;
      color?: string;
    }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/company-events/admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create event');
      return response.json();
    },

    update: async (id: string, data: any): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/company-events/admin/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update event');
      return response.json();
    },

    cancel: async (id: string, reason?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/company-events/admin/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reason }),
      });
      if (!response.ok) throw new Error('Failed to cancel event');
      return response.json();
    },

    delete: async (id: string): Promise<void> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/company-events/admin/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to delete event');
    },

    getHolidays: async (year?: number): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = year ? `?year=${year}` : '';
      const response = await fetch(`${API_BASE}/company-events/holidays${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch holidays');
      return response.json();
    },

    getUpcoming: async (limit?: number): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const params = limit ? `?limit=${limit}` : '';
      const response = await fetch(`${API_BASE}/company-events/upcoming${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch upcoming events');
      return response.json();
    },
  },

  // Dealers list (for calendar form dropdowns) — merged with main dealers namespace
  dealers: {
    getDealers: (params?: Parameters<typeof apiInstance.getDealers>[0]) => apiInstance.getDealers(params),
    getDealer: (id: string) => apiInstance.getDealer(id),
    createDealer: (data: Partial<Dealer>) => apiInstance.createDealer(data),
    updateDealer: (id: string, data: Partial<Dealer>) => apiInstance.updateDealer(id, data),
    updateDealerStatus: (id: string, status: 'active' | 'inactive') => apiInstance.updateDealerStatus(id, status),
    deleteDealer: (id: string) => apiInstance.deleteDealer(id),
    getDealerOrders: (id: string, params?: PaginationParams) => apiInstance.getDealerOrders(id, params),
    impersonateDealer: (dealerId: string, contactId?: string) => apiInstance.impersonateDealer(dealerId, contactId),
    impersonateSalesRep: (salesRepId: string) => apiInstance.impersonateSalesRep(salesRepId),
    getDealerTeam: (dealerId: string) => apiInstance.getDealerTeam(dealerId),
    list: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/dealers?limit=500`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch dealers');
      const result = await response.json();
      return result.data || result;
    },
  },

  // Consolidated shipments — group multiple orders for one dealer into a single
  // physical shipment so they ship together.
  shipments: {
    list: async (params?: { status?: string; dealerId?: string }) => {
      const token = localStorage.getItem('admin_auth_token');
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.dealerId) qs.set('dealerId', params.dealerId);
      const response = await fetch(`${API_BASE}/shipments?${qs}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch shipments');
      return response.json();
    },
    listForDealer: async (dealerId: string, includeCompleted = false) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/dealer/${dealerId}?includeCompleted=${includeCompleted}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch shipments for dealer');
      return response.json();
    },
    consolidatable: async (dealerId: string, shipmentId?: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const url = `${API_BASE}/shipments/consolidatable/${dealerId}${shipmentId ? `?shipmentId=${shipmentId}` : ''}`;
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch consolidatable orders');
      return response.json();
    },
    get: async (id: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${id}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch shipment');
      return response.json();
    },
    getDimensions: async (id: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${id}/dimensions`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch shipment dimensions');
      return response.json();
    },
    create: async (body: { dealerId: string; orderIds: string[]; notes?: string }) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to create shipment');
      }
      return response.json();
    },
    addOrder: async (shipmentId: string, orderId: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${shipmentId}/orders/${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to add order to shipment');
      }
      return response.json();
    },
    removeOrder: async (shipmentId: string, orderId: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${shipmentId}/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to remove order from shipment');
      }
      return response.json();
    },
    setQuote: async (id: string, body: any) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${id}/quote`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to set quote');
      }
      return response.json();
    },
    setOrderFreight: async (id: string, orderId: string, freightShare: number) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${id}/order-freight/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ freightShare }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to set order freight');
      }
      return response.json();
    },
    book: async (id: string, body: { bolNumber?: string; proNumber?: string; pickupConfirmation?: string }) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${id}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body || {}),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to book shipment');
      }
      return response.json();
    },
    markShipped: async (id: string, body: { trackingNumber?: string }) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${id}/mark-shipped`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body || {}),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to mark shipment shipped');
      }
      return response.json();
    },
    cancel: async (id: string) => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/shipments/${id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to cancel shipment');
      }
      return response.json();
    },
  },

  // Employees list (for calendar form dropdowns)
  employees: {
    list: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/employees`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch employees');
      return response.json();
    },
  },

  // Support tickets (factory admin oversight)
  support: {
    list: async (params: Record<string, string | number | boolean | undefined> = {}): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
      });
      const url = `${API_BASE}/support/admin/tickets${qs.toString() ? '?' + qs.toString() : ''}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch support tickets');
      return response.json();
    },
    stats: async (staleDays = 7): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/support/admin/stats?staleDays=${staleDays}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch support stats');
      return response.json();
    },
    salesReps: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/support/admin/sales-reps`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch sales reps');
      return response.json();
    },
    getById: async (id: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/support/tickets/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch ticket');
      return response.json();
    },
    update: async (
      id: string,
      body: { status?: string; priority?: string; sales_rep_id?: string | null },
    ): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/support/tickets/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update ticket');
      }
      return response.json();
    },
    addMessage: async (id: string, message: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/support/tickets/${id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message }),
      });
      if (!response.ok) throw new Error('Failed to send message');
      return response.json();
    },
  },

  // Sales tax + resale certs
  tax: {
    listCollection: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/collection`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load collection config');
      return r.json();
    },
    availableStates: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/collection/available-states`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load available states');
      return r.json();
    },
    setCollection: async (state: string, body: any): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/collection/${state}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to save collection config');
      }
      return r.json();
    },
    clearCollection: async (state: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/collection/${state}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to clear collection config');
      return r.json();
    },
    // Per-county / parish rates (destination home-rule states, e.g. LA)
    getCountyRates: async (state: string): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/counties/${state}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load county rates');
      return r.json();
    },
    setCountyRate: async (state: string, fips: string, body: { rate: number; countyName?: string; notes?: string; active?: boolean }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/counties/${state}/${fips}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('Failed to set county rate');
      return r.json();
    },
    clearCountyRate: async (state: string, fips: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/counties/${state}/${fips}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to clear county rate');
      return r.json();
    },
    setCountyBased: async (state: string, countyBased: boolean): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/collection/${state}/county-based`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ countyBased }),
      });
      if (!r.ok) throw new Error('Failed to toggle county-based');
      return r.json();
    },
    getDealerStatus: async (dealerId: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/dealer/${dealerId}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load dealer tax status');
      return r.json();
    },
    setDealerStatus: async (dealerId: string, body: { tax_exempt?: boolean; resale_cert_url?: string | null; resale_cert_notes?: string | null }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/dealer/${dealerId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update dealer tax status');
      }
      return r.json();
    },
    filingsDashboard: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/reports/filings-dashboard`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load filings dashboard');
      return r.json();
    },
    recordFiling: async (state: string, body: any): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/filings/${state}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to record filing');
      }
      return r.json();
    },
    listFilings: async (state?: string): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const qs = state ? `?state=${state}` : '';
      const r = await fetch(`${API_BASE}/tax/filings${qs}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load filings history');
      return r.json();
    },
    reportByState: async (year?: number): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const qs = year ? `?year=${year}` : '';
      const r = await fetch(`${API_BASE}/tax/reports/by-state${qs}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load tax-by-state report');
      return r.json();
    },
    salesByState: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/reports/sales-by-state`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load sales-by-state report');
      return r.json();
    },
    filingAlert: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(`${API_BASE}/tax/reports/filing-alert`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!r.ok) throw new Error('Failed to load filing alert');
      return r.json();
    },
    returnWorksheet: async (state: string, year: number, quarter: number): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const r = await fetch(
        `${API_BASE}/tax/reports/return-worksheet?state=${state}&year=${year}&quarter=${quarter}`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      );
      if (!r.ok) throw new Error('Failed to load return worksheet');
      return r.json();
    },
  },

  // QuickBooks Online integration
  quickbooks: {
    getConnectUrl: async (): Promise<{ authUrl: string }> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/quickbooks/connect`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to get QuickBooks connect URL');
      return response.json();
    },
    getStatus: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/quickbooks/status`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to get QuickBooks status');
      return response.json();
    },
    getSyncLog: async (page = 1, limit = 50): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/quickbooks/sync-log?page=${page}&limit=${limit}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to get sync log');
      return response.json();
    },
    syncCustomers: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/quickbooks/sync-customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to sync customers');
      return response.json();
    },
    syncInvoices: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/quickbooks/sync-invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to sync invoices');
      return response.json();
    },
    disconnect: async (): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/quickbooks/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to disconnect');
      return response.json();
    },
  },
};

// Warranty API
export const warrantyApi = {
  // Registrations
  listRegistrations: async (params?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: any[]; total: number; totalPages: number }> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);

    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/registrations?${searchParams.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) throw new Error('Failed to fetch registrations');
    return response.json();
  },

  getRegistration: async (id: string): Promise<any> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/registrations/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) throw new Error('Failed to fetch registration');
    return response.json();
  },

  approveRegistration: async (id: string): Promise<any> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/registrations/${id}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) throw new Error('Failed to approve registration');
    return response.json();
  },

  // RMAs
  listRmas: async (params?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: any[]; total: number; totalPages: number }> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);

    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/rma?${searchParams.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) throw new Error('Failed to fetch RMAs');
    return response.json();
  },

  getRmaQueue: async (params?: { page?: number; pageSize?: number }): Promise<{ data: any[]; total: number; totalPages: number }> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/rma/queue?${searchParams.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) throw new Error('Failed to fetch RMA queue');
    return response.json();
  },

  getRma: async (id: string): Promise<any> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/rma/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) throw new Error('Failed to fetch RMA');
    return response.json();
  },

  updateRma: async (id: string, data: { status?: string; adminNotes?: string }): Promise<any> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/rma/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) throw new Error('Failed to update RMA');
    return response.json();
  },

  approveRma: async (id: string, adminNotes?: string): Promise<any> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/rma/${id}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ adminNotes }),
    });

    if (!response.ok) throw new Error('Failed to approve RMA');
    return response.json();
  },

  denyRma: async (id: string, denialReason: string, adminNotes?: string): Promise<any> => {
    const token = localStorage.getItem('admin_auth_token');
    const response = await fetch(`${API_BASE}/warranty/rma/${id}/deny`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ denialReason, adminNotes }),
    });

    if (!response.ok) throw new Error('Failed to deny RMA');
    return response.json();
  },

  // Commissions / Rep Firm Payouts
  commissions: {
    getPayoutSummary: async (params?: { start_date?: string; end_date?: string; status?: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const qs = new URLSearchParams();
      if (params?.start_date) qs.set('start_date', params.start_date);
      if (params?.end_date) qs.set('end_date', params.end_date);
      if (params?.status) qs.set('status', params.status);
      const response = await fetch(`${API_BASE}/rep-firms/commissions/payouts?${qs}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch commission payouts');
      return response.json();
    },
    getDetails: async (params?: { rep_firm_id?: string; sales_rep_id?: string; status?: string; start_date?: string; end_date?: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const qs = new URLSearchParams();
      if (params?.rep_firm_id) qs.set('rep_firm_id', params.rep_firm_id);
      if (params?.sales_rep_id) qs.set('sales_rep_id', params.sales_rep_id);
      if (params?.status) qs.set('status', params.status);
      if (params?.start_date) qs.set('start_date', params.start_date);
      if (params?.end_date) qs.set('end_date', params.end_date);
      const response = await fetch(`${API_BASE}/rep-firms/commissions/details?${qs}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch commission details');
      return response.json();
    },
    markPaid: async (commissionIds: string[]): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/rep-firms/commissions/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ commission_ids: commissionIds }),
      });
      if (!response.ok) throw new Error('Failed to mark commissions as paid');
      return response.json();
    },
  },
  paymentAudit: {
    list: async (params?: { page?: number; pageSize?: number; search?: string; paymentMethod?: string; dateFrom?: string; dateTo?: string }): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const sp = new URLSearchParams();
      if (params?.page) sp.set('page', params.page.toString());
      if (params?.pageSize) sp.set('pageSize', params.pageSize.toString());
      if (params?.search) sp.set('search', params.search);
      if (params?.paymentMethod) sp.set('paymentMethod', params.paymentMethod);
      if (params?.dateFrom) sp.set('dateFrom', params.dateFrom);
      if (params?.dateTo) sp.set('dateTo', params.dateTo);
      const response = await fetch(`${API_BASE}/rewards/admin/payment-audit?${sp.toString()}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch payment audit');
      return response.json();
    },
  },
  referralRewards: {
    list: async (): Promise<any[]> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/rewards/admin/referral-rewards`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch referral rewards');
      return response.json();
    },
    award: async (referringDealerId: string, referredDealerId: string, notes?: string): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/rewards/admin/referral-reward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ referringDealerId, referredDealerId, notes }),
      });
      if (!response.ok) throw new Error('Failed to award referral');
      return response.json();
    },
  },
  recentAwards: {
    list: async (limit = 20, offset = 0): Promise<any> => {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE}/rewards/admin/recent-awards?limit=${limit}&offset=${offset}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch recent awards');
      return response.json();
    },
  },

  // Anti-scraping security dashboard. All endpoints are admin-gated server-side
  // (SecurityController.requireAdmin); the client only reads/acts on them.
  security: {
    _headers: () => {
      const token = localStorage.getItem('admin_auth_token');
      return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    },
    async _get(path: string) {
      const response = await fetch(`${API_BASE}/security${path}`, { headers: api.security._headers() });
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.message || `HTTP ${response.status}`);
      }
      return response.json();
    },
    async _post(path: string, body?: any) {
      const response = await fetch(`${API_BASE}/security${path}`, {
        method: 'POST', headers: api.security._headers(), body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || `HTTP ${response.status}`);
      return data;
    },
    getStats: () => api.security._get('/stats'),
    getMode: () => api.security._get('/mode'),
    getLocks: () => api.security._get('/locks'),
    getEvents: (limit = 100) => api.security._get(`/events?limit=${limit}`),
    revokeSessions: (type: string, id: string) => api.security._post(`/users/${type}/${id}/revoke-sessions`),
    lock: (type: string, id: string, body: { reason?: string; durationMinutes?: number; hard?: boolean }) =>
      api.security._post(`/users/${type}/${id}/lock`, body),
    unlock: (type: string, id: string) => api.security._post(`/users/${type}/${id}/unlock`),
  },
};

export default api;

// Re-export for convenience
export { ApiService };